<template>
  <div class="contemporary-group">
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-card color="transparent" flat>
      <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="editContemporaryGroup">
        <v-toolbar color="transparent" flat>
          <v-toolbar-title class="font-weight-bold">{{ $t('edit')}} {{ traits.find(item => item.value === $route.params.trait)?.label }} {{$t('geneticsAndBreeding.contemporaryGroup')}}</v-toolbar-title>

          <v-spacer></v-spacer>
          <v-btn outlined depressed color="primary" class="mr-2" @click="$router.push({ name: 'contemporaryGroups', query: { trait } })">{{ $t('cancel')}}</v-btn>
          <v-btn color="primary" type="submit">{{ $t('update')}}</v-btn>
        </v-toolbar>

        <v-card-text class="v-card__body">
          <div class="d-flex pa-2 align-center">
            <h3 class="black--text">{{ $t('geneticsAndBreeding.editGroup')}}</h3>

            <v-spacer></v-spacer>

            <v-btn
              depressed
              color="error"
              @click="confirmDialog=true"
            >
             {{ $t('geneticsAndBreeding.delete')}}
            </v-btn>
          </div>

          <hr class="grey-hr" />

          <v-card-text class="mt-5 pa-0">
            <v-row>
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.selectTrait')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-select :items="traits" v-model="trait" outlined dense
                  @change="getGroupCodes"
                  item-text="label"
                  item-value="value"
                  readonly
                  style="background-color: rgb(238, 238, 238);"
                  hide-details
                  ></v-select>
              </v-col>
  
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.contemporaryGroupName')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-text-field dense v-model="contemporaryGroupName" :placeholder="$t('geneticsAndBreeding.enterNameHere')" outlined
                :rules="contemporaryGroupRules.groupName"
                :class="{'required-field': contemporaryGroupRules.groupName}" 
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>        
  
          <v-card-text class="mt-2 pa-0" v-if="trait === 'birthWeight'">
            <h3 class="black--text pb-5">{{ $t('geneticsAndBreeding.addBirthWeightData')}}</h3>
          
            <hr class="grey-hr" />
          
            <v-row class="mt-5">
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.birthYearRange')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-row>
                  <v-col cols="6">
                    <v-menu ref="fromMenu" v-model="fromMenu" :close-on-content-click="false"
                      transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="birthWeightContemporaryGroupData.birthYearFrom"
                        placeholder="From" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                        outlined
                        :rules="requiredFieldRules.birthYearRange"
                        :class="{'required-field': (requiredFieldsArray.birthYearRange==true)}"
                        v-on="on"></v-text-field>
                      </template>
                      <v-date-picker v-model="birthWeightContemporaryGroupData.birthYearFrom" no-title scrollable>
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="toMenu = false">
                         {{ $t('cancel')}}
                        </v-btn>
                        <v-btn text color="primary" @click="$refs.fromMenu.save(birthWeightContemporaryGroupData.birthYearFrom)">
                          {{ $t('ok')}}
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu ref="toMenu" v-model="toMenu" :close-on-content-click="false"
                      transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="birthWeightContemporaryGroupData.birthYearTo"
                        placeholder="To" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                        outlined
                        :rules="requiredFieldRules.birthYearRange"
                        :class="{'required-field': (requiredFieldsArray.birthYearRange==true)}"
                        v-on="on"></v-text-field>
                      </template>
                      <v-date-picker v-model="birthWeightContemporaryGroupData.birthYearTo" no-title scrollable>
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="toMenu = false">
                          {{ $t('cancel')}}
                        </v-btn>
                        <v-btn text color="primary" @click="$refs.toMenu.save(birthWeightContemporaryGroupData.birthYearTo)">
                          {{ $t('ok')}}
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.birthSex')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-select :items="sex" placeholder="Select Sex" v-model="birthWeightContemporaryGroupData.birthSex" outlined dense
                :rules="requiredFieldRules.birthSex"
                :class="{'required-field': (requiredFieldsArray.birthSex==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="4">
              <label class="input-label">{{ $t('geneticsAndBreeding.breederHerdCode')}}
                <v-tooltip top color="black" max-width="250">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                    v-bind="attrs"
                    v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span>{{ $t('geneticsAndBreeding.breederHerdCode') }}</span>
                </v-tooltip>
              </label>
              <v-text-field
                dense v-model="birthWeightContemporaryGroupData.breederHerdCode" :placeholder="this.$t('geneticsAndBreeding.breederHerdCode')" outlined
                :rules="requiredFieldRules.breederHerdCode"
                :class="{'required-field': (requiredFieldsArray.breederHerdCode==true)}"
                maxlength="10"
              ></v-text-field>
            </v-col>

            <v-col cols="4">
              <label class="input-label">{{ $t('geneticsAndBreeding.birthManagementCode')}}
                <v-tooltip top color="black" max-width="250">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                    v-bind="attrs"
                    v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span>{{ $t('geneticsAndBreeding.birthManagementCode') }}</span>
                </v-tooltip>
              </label>
              <v-text-field
                dense v-model="birthWeightContemporaryGroupData.birthManagementCode" :placeholder="$t('geneticsAndBreeding.birthManagementCode')" outlined
                :rules="requiredFieldRules.birthManagementCode"
                :class="{'required-field': (requiredFieldsArray.birthManagementCode==true)}"
                maxlength="10"
              ></v-text-field>
            </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.seasonofBirth')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-row>
                  <v-col cols="6">
                    <v-select :items="months" item-text="label" item-value="value"
                    v-model="birthWeightContemporaryGroupData.seasonOfBirthFrom" outlined dense
                    placeholder="From"
                    :rules="requiredFieldRules.seasonOfBirth"
                    :class="{'required-field': (requiredFieldsArray.seasonOfBirth==true)}"
                    ></v-select>
                  </v-col>
                  <v-col cols="6">
                    <v-select :items="months" item-text="label" item-value="value"
                    v-model="birthWeightContemporaryGroupData.seasonOfBirthTo" outlined dense
                    placeholder="To"
                    :rules="requiredFieldRules.seasonOfBirth"
                    :class="{'required-field': (requiredFieldsArray.seasonOfBirth==true)}"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.primaryBreed')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-select :items="breeds" :placeholder="this.$t('geneticsAndBreeding.selectBreed')" dense v-model="birthWeightContemporaryGroupData.primaryBreed" outlined
                :rules="requiredFieldRules.primaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.primaryBreedComposition==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.breedComposition')}}
                  <v-tooltip top color="black" max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ $t("epd.breedComposition") }}</span>
                </v-tooltip>
                </label>
                <v-text-field type="Number" dense v-model="birthWeightContemporaryGroupData.primaryBreedComposition" placeholder="e.g 80%" outlined
                :rules="requiredFieldRules.primaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.primaryBreedComposition==true)}" 
                :error-messages="bPBCerror" min="0" max="100"
                ></v-text-field>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.secondaryBreed')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-select :items="breeds" :placeholder="this.$t('geneticsAndBreeding.selectBreed')" dense v-model="birthWeightContemporaryGroupData.secondaryBreed" outlined
                :rules="requiredFieldRules.secondaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.secondaryBreedComposition==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.breedComposition')}}
                  <v-tooltip top color="black" max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ $t("epd.breedComposition") }}</span>
                </v-tooltip>
                </label>
                <v-text-field type="Number" dense v-model="birthWeightContemporaryGroupData.secondaryBreedComposition" placeholder="e.g 80%" outlined
                :rules="requiredFieldRules.secondaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.secondaryBreedComposition==true)}"
                :error-messages="bSBCerror" min="0" max="100"
                ></v-text-field>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.conceptionMethod')}}
                  <v-tooltip top color="black" max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                      v-bind="attrs"
                      v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('detailedDescription')}}</span>
                  </v-tooltip>
                </label>
                <v-select :items="conceptionMethods" v-model="birthWeightContemporaryGroupData.conceptionMethod" outlined dense
                :rules="requiredFieldRules.conceptionMethod"
                :class="{'required-field': (requiredFieldsArray.conceptionMethod==true)}"
                  ></v-select>
              </v-col>
          
            </v-row>
          </v-card-text>
          
          <v-card-text class="mt-2 pa-0" v-if="trait === 'weaningWeight'">
            <h3 class="black--text pb-5">{{ $t('geneticsAndBreeding.addWeaningWeightData')}}</h3>
          
            <hr class="grey-hr" />
          
            <v-row class="mt-5">
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.birthWeightContemporaryGroupCode')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="groupCodes"
                item-text="group_code"
                item-value="id"
                v-model="weaningWeightContemporaryGroupData.birthWeightContemporaryGroupId"
                outlined dense
                placeholder="E.g WW0000001"
                :rules="requiredFieldRules.birthWeightContempraryGroupCode"
                :class="{'required-field': (requiredFieldsArray.birthWeightContempraryGroupCode==true)}"
                >
              </v-select>
              </v-col>
          
              <v-col cols="4">
              <label class="input-label">{{ $t('geneticsAndBreeding.managementPastureCode')}}
                <v-tooltip top color="black" max-width="250">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                    v-bind="attrs"
                    v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span>{{ $t('geneticsAndBreeding.managementPastureCode') }}</span>
                </v-tooltip>
              </label>
              <v-text-field
                dense v-model="weaningWeightContemporaryGroupData.managementPastureCode" :placeholder="this.$t('geneticsAndBreeding.managementPastureCode')" outlined
                :rules="requiredFieldRules.managementPastureCode"
                :class="{'required-field': (requiredFieldsArray.managementPastureCode==true)}"
                maxlength="10"
              ></v-text-field>
            </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.dateWeighted')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-row>
                  <v-col cols="6">
                    <v-menu ref="fromMenu" v-model="fromMenu" :close-on-content-click="false"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="weaningWeightContemporaryGroupData.dateWeightedFrom"
                      placeholder="From" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      v-on="on"
                      :rules="requiredFieldRules.dateWeighing"
                      :class="{'required-field': (requiredFieldsArray.dateWeighing==true)}"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="weaningWeightContemporaryGroupData.dateWeightedFrom" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="fromMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.fromMenu.save(weaningWeightContemporaryGroupData.dateWeightedFrom)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu ref="toMenu" v-model="toMenu" :close-on-content-click="false"
                    transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="weaningWeightContemporaryGroupData.dateWeightedTo"
                      placeholder="To" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      v-on="on"
                      :rules="requiredFieldRules.dateWeighing"
                      :class="{'required-field': (requiredFieldsArray.dateWeighing==true)}"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="weaningWeightContemporaryGroupData.dateWeightedTo" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="toMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.toMenu.save(weaningWeightContemporaryGroupData.dateWeightedTo)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.weaningSex')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="sexOptions" v-model="weaningWeightContemporaryGroupData.weaningSex" outlined dense
                :rules="requiredFieldRules.weaningSex"
                :class="{'required-field': (requiredFieldsArray.weaningSex==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.primaryBreed')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="breeds" :placeholder="this.$t('geneticsAndBreeding.selectBreed')" dense v-model="weaningWeightContemporaryGroupData.primaryBreed" outlined
                :rules="requiredFieldRules.primaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.primaryBreedComposition==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.breedComposition')}}</label>
                <v-text-field type="Number" dense v-model="weaningWeightContemporaryGroupData.primaryBreedComposition" placeholder="e.g 80%" outlined
                :rules="requiredFieldRules.primaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.primaryBreedComposition==true)}"
                :error-messages="wPBCerror" min="0" max="100"
                ></v-text-field>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.secondaryBreed')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="breeds" :placeholder="this.$t('geneticsAndBreeding.selectBreed')" dense v-model="weaningWeightContemporaryGroupData.secondaryBreed" outlined
                :rules="requiredFieldRules.secondaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.secondaryBreedComposition==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="2">
                <label class="input-label">{{ $t('geneticsAndBreeding.breedComposition')}}</label>
                <v-text-field type="Number" dense v-model="weaningWeightContemporaryGroupData.secondaryBreedComposition" placeholder="e.g 80%" outlined
                :rules="requiredFieldRules.secondaryBreedComposition"
                :class="{'required-field': (requiredFieldsArray.secondaryBreedComposition==true)}"
                :error-messages="wPBCerror" min="0" max="100"
                ></v-text-field>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.conceptionMethod')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="conceptionMethods" v-model="weaningWeightContemporaryGroupData.conceptionMethod" outlined dense
                :rules="requiredFieldRules.conceptionMethod"
                :class="{'required-field': (requiredFieldsArray.conceptionMethod==true)}"
                  ></v-select>
              </v-col>
          
            </v-row>
          </v-card-text>
          
          <v-card-text class="mt-2 pa-0" v-if="trait === 'yearlingWeight'">
            <h3 class="black--text pb-5">{{ $t('geneticsAndBreeding.addYearlingWeightData')}}</h3>
          
            <hr class="grey-hr" />
          
            <v-row class="mt-5">
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.weaningContemporaryGroupCode')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="groupCodes"
                item-text="group_code"
                item-value="id"
                v-model="yearlingWeightContemporaryGroupData.weaningWeightContemporaryGroupId"
                outlined dense
                placeholder="E.g WW0000001"
                :rules="requiredFieldRules.weaningContempararyGroupCode"
                :class="{'required-field': (requiredFieldsArray.weaningContempararyGroupCode==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="4">
              <label class="input-label">{{ $t('geneticsAndBreeding.managementFeedingCode')}}
                <v-tooltip top color="black" max-width="250">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                    v-bind="attrs"
                    v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span>{{ $t('geneticsAndBreeding.managementFeedingCode') }}</span>
                </v-tooltip>
              </label>
              <v-text-field
                dense v-model="yearlingWeightContemporaryGroupData.managementFeedingCode" :placeholder="this.$t('geneticsAndBreeding.managementFeedingCode')" outlined
                :rules="requiredFieldRules.managementFeedingBatchCode"
                :class="{'required-field': (requiredFieldsArray.managementFeedingBatchCode==true)}"
                maxlength="10"
              ></v-text-field>
              </v-col>
          
              <v-col cols="4"> 
                <label class="input-label">{{ $t('geneticsAndBreeding.dateWeighted')}}<v-icon>mdi-information-outline</v-icon></label>
                <v-row>
                  <v-col cols="6">
                    <v-menu ref="fromMenu" v-model="fromMenu" :close-on-content-click="false"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="yearlingWeightContemporaryGroupData.dateWeightedFrom"
                      placeholder="From" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.dateWeighing"
                      :class="{'required-field': (requiredFieldsArray.dateWeighing==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="yearlingWeightContemporaryGroupData.dateWeightedFrom" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="fromMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.fromMenu.save(yearlingWeightContemporaryGroupData.dateWeightedFrom)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu ref="toMenu" v-model="toMenu" :close-on-content-click="false"
                    transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="yearlingWeightContemporaryGroupData.dateWeightedTo"
                      placeholder="To" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.dateWeighing"
                      :class="{'required-field': (requiredFieldsArray.dateWeighing==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="yearlingWeightContemporaryGroupData.dateWeightedTo" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="toMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.toMenu.save(yearlingWeightContemporaryGroupData.dateWeightedTo)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.yearlingSex')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="sexOptions" v-model="yearlingWeightContemporaryGroupData.yearlingSex" outlined dense
                :rules="requiredFieldRules.yearlingSex"
                :class="{'required-field': (requiredFieldsArray.yearlingSex==true)}"
                ></v-select>
              </v-col>
          
            </v-row>
          </v-card-text>
          
          <v-card-text class="mt-2 pa-0" v-if="trait === 'carcassWeight'">
            <h3 class="black--text pb-5">{{ $t('geneticsAndBreeding.addCarcassWeightData')}}</h3>
          
            <hr class="grey-hr" />
          
            <v-row class="mt-5">
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.weaningOrYearlingContemporaryGroupCode')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-row>
                  <v-col cols="6">
                    <v-select :items="carcassTraitOptions"
                    v-model="carcassTrait" outlined dense
                    @change="getGroupCodes"
                    item-text="label"
                    item-value="value"
                    :placeholder="this.$t('selectOption')"
                    :rules="requiredFieldRules.weaningYearlingWeightCode"
                    :class="{'required-field': (requiredFieldsArray.weaningYearlingWeightCode==true)}"
                    ></v-select>
                  </v-col>
                  <v-col cols="6">
                    <v-select :items="groupCodes"
                    item-text="group_code"
                    item-value="id"
                    v-model="carcassWeightContemporaryGroupData.traitGroupCode"
                    outlined dense
                    placeholder="E.g WW0000001"
                    :rules="requiredFieldRules.weaningYearlingWeightCode"
                    :class="{'required-field': (requiredFieldsArray.weaningYearlingWeightCode==true)}"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.daysOnFeed')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-row>
                  <v-col cols="6">
                    <v-text-field type="Number"
                    min="0"
                    dense v-model="carcassWeightContemporaryGroupData.daysOnFeed" :placeholder="this.$t('geneticsAndBreeding.enterNumber')" outlined
                    :rules="requiredFieldRules.daysOnFeed"
                    :class="{'required-field': (requiredFieldsArray.daysOnFeed==true)}"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field readonly
                    dense value="Days" outlined></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.carcassSex')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select :items="sex" v-model="carcassWeightContemporaryGroupData.carcassSex" outlined dense
                :rules="requiredFieldRules.carcassSex"
                :class="{'required-field': (requiredFieldsArray.carcassSex==true)}"
                ></v-select>
              </v-col>
          
              <v-col cols="4">
              <label class="input-label">{{ $t('geneticsAndBreeding.managementFeedingCode')}}
                <v-tooltip top color="black" max-width="250">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                    v-bind="attrs"
                    v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span>{{ $t('geneticsAndBreeding.managementFeedingCode') }}</span>
                </v-tooltip>
              </label>
              <v-text-field
                dense v-model="carcassWeightContemporaryGroupData.managementPenFeedingCode" :placeholder="this.$t('geneticsAndBreeding.managementFeedingCode')" outlined
                :rules="requiredFieldRules.managementPenFeedingBatch"
                :class="{'required-field': (requiredFieldsArray.managementPenFeedingBatch==true)}"
                maxlength="10"
              ></v-text-field>
            </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.breedOfDom')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-select 
                :items="breeds" 
                :placeholder="this.$t('geneticsAndBreeding.selectBreed')" 
                dense 
                v-model="carcassWeightContemporaryGroupData.breedOfDom" 
                outlined
                :rules="requiredFieldRules.breedOfDam"
                :class="{'required-field': (requiredFieldsArray.breedOfDam==true)}"
              ></v-select>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.harvestDate')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-row>
                  <v-col cols="6">
                    <v-menu ref="harvestFromMenu" v-model="harvestFromMenu" :close-on-content-click="false"
                    transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="carcassWeightContemporaryGroupData.harvestDateFrom"
                      placeholder="From" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.harvestDate"
                      :class="{'required-field': (requiredFieldsArray.harvestDate==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="carcassWeightContemporaryGroupData.harvestDateFrom" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="harvestFromMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.harvestFromMenu.save(carcassWeightContemporaryGroupData.harvestDateFrom)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu ref="harvestToMenu" v-model="harvestToMenu" :close-on-content-click="false" :return-value.sync="date"
                    transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="carcassWeightContemporaryGroupData.harvestDateTo"
                      placeholder="To" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.harvestDate"
                      :class="{'required-field': (requiredFieldsArray.harvestDate==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="carcassWeightContemporaryGroupData.harvestDateTo" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="harvestToMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.harvestToMenu.save(carcassWeightContemporaryGroupData.harvestDateTo)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.gradingDate')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-row>
                  <v-col cols="6">
                    <v-menu ref="gradingFromMenu" v-model="gradingFromMenu" :close-on-content-click="false"
                    transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="carcassWeightContemporaryGroupData.gradingDateFrom"
                      placeholder="From" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.gradingDate"
                      :class="{'required-field': (requiredFieldsArray.gradingDate==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="carcassWeightContemporaryGroupData.gradingDateFrom" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="gradingFromMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.gradingFromMenu.save(carcassWeightContemporaryGroupData.gradingDateFrom)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu ref="gradingToMenu" v-model="gradingToMenu" :close-on-content-click="false"
                    transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="carcassWeightContemporaryGroupData.gradingDateTo"
                      placeholder="To" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.gradingDate"
                      :class="{'required-field': (requiredFieldsArray.gradingDate==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="carcassWeightContemporaryGroupData.gradingDateTo" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="gradingToMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.gradingToMenu.save(carcassWeightContemporaryGroupData.gradingDateTo)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                </v-row>
              </v-col>
          
              <v-col cols="4">
                <label class="input-label">{{ $t('geneticsAndBreeding.dateOnFeed')}} <v-icon>mdi-information-outline</v-icon></label>
                <v-row>
                  <v-col cols="6">
                    <v-menu ref="feedFromMenu" v-model="feedFromMenu" :close-on-content-click="false"
                    transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="carcassWeightContemporaryGroupData.dateOnFeedFrom"
                      placeholder="From" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.dateOnFeed"
                      :class="{'required-field':  (requiredFieldsArray.dateOnFeed==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="carcassWeightContemporaryGroupData.dateOnFeedFrom" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="feedFromMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.feedFromMenu.save(carcassWeightContemporaryGroupData.dateOnFeedFrom)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu ref="feedToMenu" v-model="feedToMenu" :close-on-content-click="false" :return-value.sync="date"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="carcassWeightContemporaryGroupData.dateOnFeedTo"
                      placeholder="To" dense append-icon="mdi-calendar" readonly v-bind="attrs"
                      outlined
                      :rules="requiredFieldRules.dateOnFeed"
                      :class="{'required-field': (requiredFieldsArray.dateOnFeed==true)}"
                      v-on="on"></v-text-field>
                    </template>
                    <v-date-picker v-model="carcassWeightContemporaryGroupData.dateOnFeedTo" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="feedToMenu = false">
                        {{ $t('cancel')}}
                      </v-btn>
                      <v-btn text color="primary" @click="$refs.feedToMenu.save(carcassWeightContemporaryGroupData.dateOnFeedTo)">
                        {{ $t('ok')}}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  </v-col>
                </v-row>
              </v-col>
          
            </v-row>
          </v-card-text>

        </v-card-text>
      </v-form>

      <v-dialog v-model="confirmDialog" width="500" @click:outside="confirmDialog=false" :retain-focus="false">
        <v-card class="custom-card" rounded="0">
          <v-toolbar color="secondary" class="white--text" flat>
            <v-toolbar-title class="font-weight-bold">
              {{ $t('warning')}}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon class="white--text" @click="confirmDialog=false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-form lazy-validation autocomplete="off">
            <v-card-text class="black--text">
              <v-row>
                <v-col cols="12" >
                  <div class="text-center">
                    <img src="../../../assets/warning.svg" />
                  </div>
                  <div class="py-5 text-center">
                    {{ $t('geneticsAndBreeding.deleteContemporaryGroupMsg')}}
                  </div>
                  
                </v-col>
              </v-row>
              <v-divider class="mt-5"></v-divider>
              <v-card-actions class="mt-2" style="padding-right: 0">
                <v-spacer></v-spacer>
                <v-btn outlined class="mr-2" right height="44" width="116" depressed color="primary"
                  @click="confirmDialog = false">
                  {{ $t('cancel')}}
                </v-btn>
                <v-btn right height="44" width="116" color="primary" depressed
                  @click="deleteContemporaryGroup()">
                  {{ $t('proceed')}}
                </v-btn>
              </v-card-actions>
            </v-card-text>
          </v-form>
        </v-card>
      </v-dialog>

    </v-card>
  </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import loadingMixin from "@/mixins/LoadingMixin";
import {
    breeds
} from "@/models/units.js"
import {
  contemporaryGroupRules
} from "@/validations/ContemporaryGroupRules.js"
export default {
  name: 'ContemporaryGroupsSetting',
  watch: {
    requiredFieldsArray: {
      handler(){
        this.$refs.form.validate()
      },
      deep:true,
      immediate: true
    },
    'birthWeightContemporaryGroupData.primaryBreedComposition': {
      handler(){
        if( this.birthWeightContemporaryGroupData.primaryBreedComposition > 100){
          this.birthWeightContemporaryGroupData.primaryBreedComposition = 100
        }else if( this.birthWeightContemporaryGroupData.primaryBreedComposition < 0){
          this.birthWeightContemporaryGroupData.primaryBreedComposition = 0
        }
        if((Number(this.birthWeightContemporaryGroupData.primaryBreedComposition) + Number(this.birthWeightContemporaryGroupData.secondaryBreedComposition))>100){
          this.bPBCerror = 'Breed Composition total can\'t be more then 100'
          this.bSBCerror = 'Breed Composition total can\'t be more then 100'
        }else {
          this.bPBCerror = ''
          this.bSBCerror = ''
        }
      },
      deep:true,
      immediate: true
    },
    'birthWeightContemporaryGroupData.secondaryBreedComposition': {
      handler(){
        if( this.birthWeightContemporaryGroupData.secondaryBreedComposition > 100){
          this.birthWeightContemporaryGroupData.secondaryBreedComposition = 100
        }else if( this.birthWeightContemporaryGroupData.secondaryBreedComposition < 0){
          this.birthWeightContemporaryGroupData.secondaryBreedComposition = 0
        }
        if((Number(this.birthWeightContemporaryGroupData.primaryBreedComposition) + Number(this.birthWeightContemporaryGroupData.secondaryBreedComposition))>100){
          this.bPBCerror = 'Breed Composition total can\'t be more then 100'
          this.bSBCerror = 'Breed Composition total can\'t be more then 100'
        }else {
          this.bPBCerror = ''
          this.bSBCerror = ''
        }
      },
      deep:true,
      immediate: true
    },
    'weaningWeightContemporaryGroupData.primaryBreedComposition': {
      handler(){
        if( this.weaningWeightContemporaryGroupData.primaryBreedComposition > 100){
          this.weaningWeightContemporaryGroupData.primaryBreedComposition = 100
        }else if( this.weaningWeightContemporaryGroupData.primaryBreedComposition < 0){
          this.weaningWeightContemporaryGroupData.primaryBreedComposition = 0
        }
        if((Number(this.weaningWeightContemporaryGroupData.primaryBreedComposition) + Number(this.weaningWeightContemporaryGroupData.secondaryBreedComposition))>100){
          this.wPBCerror = 'Breed Composition total can\'t be more then 100'
          this.wSBCerror = 'Breed Composition total can\'t be more then 100'
        }else {
          this.wPBCerror = ''
          this.wSBCerror = ''
        }
      },
      deep:true,
      immediate: true
    },
    'weaningWeightContemporaryGroupData.secondaryBreedComposition': {
      handler(){
        if( this.weaningWeightContemporaryGroupData.secondaryBreedComposition > 100){
          this.weaningWeightContemporaryGroupData.secondaryBreedComposition = 100
        }else if( this.weaningWeightContemporaryGroupData.secondaryBreedComposition < 0){
          this.weaningWeightContemporaryGroupData.secondaryBreedComposition = 0
        }
        if((Number(this.weaningWeightContemporaryGroupData.primaryBreedComposition) + Number(this.weaningWeightContemporaryGroupData.secondaryBreedComposition))>100){
          this.wPBCerror = 'Breed Composition total can\'t be more then 100'
          this.wSBCerror = 'Breed Composition total can\'t be more then 100'
        }else {
          this.wPBCerror = ''
          this.wSBCerror = ''
        }
      },
      deep:true,
      immediate: true
    },
    'carcassWeightContemporaryGroupData.daysOnFeed':{
      handler(){
        if(this.carcassWeightContemporaryGroupData.daysOnFeed<0){
          this.carcassWeightContemporaryGroupData.daysOnFeed=0.0;
        }
      }
    }
  },
  data() {
    return {
      bPBCerror: '',
      wPBCerror: '',
      bSBCerror: '',
      wSBCerror: '',
      breadcrumbs: [
        {
          text: this.$t('dashboard'),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t('geneticsAndBreeding.geneticsAndBreeding'),
          disabled: false,
          link: true,
          exact: true,
          to: { name: "Genetics" },
        },
        {
          text: this.$t('geneticsAndBreeding.contemporaryGroupsSettings'),
          disabled: true
        },
      ],
      traits: [
        {
          label: this.$t('geneticsAndBreeding.traitsType.birthWeight'),
          value: 'birthWeight'
        },
        {
          label: this.$t('geneticsAndBreeding.traitsType.weaningWeight'),
          value: 'weaningWeight'
        },
        {
          label: this.$t('geneticsAndBreeding.traitsType.yearlingWeight'),
          value: 'yearlingWeight'
        },
        {
          label: this.$t('geneticsAndBreeding.traitsType.carcassWeight'),
          value: 'carcassWeight'
        }
      ],
      sex: [
        'Male',
        'Female'
      ],
      sexOptions:[
        'Male',
        'Female',
        'Castrated'
      ],
      carcassTraitOptions: [
        {
          label: this.$t('geneticsAndBreeding.traitsType.weaningWeight'),
          value: 'weaningWeight'
        },
        {
          label: this.$t('geneticsAndBreeding.traitsType.yearlingWeight'),
          value: 'yearlingWeight'
        }
      ],
      trait: 'birthWeight',
      date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      fromMenu: false,
      toMenu: false,
      harvestFromMenu: false,
      harvestToMenu: false,
      gradingFromMenu: false,
      gradingToMenu: false,
      feedFromMenu: false,
      feedToMenu: false,
      conceptionMethods: ['Natural Mating', 'Artificial Insemination', 'Embryo Transfer'],
      // conceptionMethods: [
      //   this.$t('animal.naturalMating'),
      //   this.$t('animal.artificialInsemination'),
      //   this.$t('animal.embryoTransfer'),
      // ],
      contemporaryGroupName: '',
      birthWeightContemporaryGroupData: {},
      weaningWeightContemporaryGroupData: {},
      yearlingWeightContemporaryGroupData: {},
      carcassWeightContemporaryGroupData: {},
      contemporaryGroupData: {},
      groupCodes: [],
      carcassTrait: '',
      breeds,
      months: [
        {
          label: 'Jan',
          value: '1'
        },
        {
          label: 'Feb',
          value: '2'
        },
        {
          label: 'Mar',
          value: '3'
        },
        {
          label: 'Apr',
          value: '4'
        },
        {
          label: 'May',
          value: '5'
        },
        {
          label: 'Jun',
          value: '6'
        },
        {
          label: 'Jul',
          value: '7'
        },
        {
          label: 'Aug',
          value: '8'
        },
        {
          label: 'Sep',
          value: '9'
        },
        {
          label: 'Oct',
          value: '10'
        },
        {
          label: 'Nov',
          value: '11'
        },
        {
          label: 'Dec',
          value: '12'
        }
      ],
      confirmDialog: false,
      contemporaryGroupRules,
      valid: true,
      requiredFields: [],
      requiredFieldRules: {},
      requiredFieldsArray: {},
    }
  },
  methods: {
    snakeCaseToCamelCase (key) {
      return key.toLowerCase().split("_").map(((part, i) => (i > 0) ? part.charAt(0).toUpperCase() + part.slice(1) : part)).join("");
    },
    async getContemporaryGroupData () {
      this.startLoading();
      const { id, trait } = this.$route.params
      const res = await axios.get(`animalGenetics/contemporaryGroup/${trait}/${id}`)
      if (res.data.success) {
        const item = res.data.data
        for (const [key, value] of Object.entries(item)) {
          delete item[key]
          const sanitizedKey = this.snakeCaseToCamelCase(key)
          item[sanitizedKey] = value
        }
        this.contemporaryGroupName = item.groupName
        if (trait === 'birthWeight') {
          item.birthYearTo = this.formatDate(item.birthYearTo)
          item.birthYearFrom = this.formatDate(item.birthYearFrom)
          this.birthWeightContemporaryGroupData = item
        } else if (trait === 'weaningWeight') {
          item.dateWeightedTo = this.formatDate(item.dateWeightedTo)
          item.dateWeightedFrom = this.formatDate(item.dateWeightedFrom)
          this.weaningWeightContemporaryGroupData = item
        } else if (trait === 'yearlingWeight') {
          item.dateWeightedTo = this.formatDate(item.dateWeightedTo)
          item.dateWeightedFrom = this.formatDate(item.dateWeightedFrom)
          this.yearlingWeightContemporaryGroupData = item
        } else if (trait === 'carcassWeight') {
          item.harvestDateTo = this.formatDate(item.harvestDateTo)
          item.harvestDateFrom = this.formatDate(item.harvestDateFrom)

          item.gradingDateTo = this.formatDate(item.gradingDateTo)
          item.gradingDateFrom = this.formatDate(item.gradingDateFrom)

          item.dateOnFeedTo = this.formatDate(item.dateOnFeedTo)
          item.dateOnFeedFrom = this.formatDate(item.dateOnFeedFrom)
          if (item.weaningWeightContemporaryGroupId) {
            this.carcassTrait = 'weaningWeight'
            item.traitGroupCode = item?.weaningWeightContemporaryGroupId
          } else {
            this.carcassTrait = 'yearlingWeight'
            item.traitGroupCode = item?.yearlingWeightContemporaryGroupId
          }
          this.carcassWeightContemporaryGroupData = item
          this.stopLoading();
          this.getGroupCodes()
        }
      } else {
        this.$notify({
          title: this.$t('geneticsAndBreeding.errorFetchingContemporaryGrpMsg'),
          text: res.data.message,
          type: "error",
        });
        this.stopLoading();
      }
      this.stopLoading();
    },
    async editContemporaryGroup() {
      if (!this.$refs.form.validate()) {
        return;
      }
      this.startLoading();
      let data
      if (this.trait === 'birthWeight') {
        data = this.birthWeightContemporaryGroupData
      } else if (this.trait === 'weaningWeight') {
        data = this.weaningWeightContemporaryGroupData
      } else if (this.trait === 'yearlingWeight') {
        data = this.yearlingWeightContemporaryGroupData
      } else if (this.trait === 'carcassWeight') {
        data = this.carcassWeightContemporaryGroupData
        if (this.carcassTrait === 'weaningWeight') {
          data['weaningWeightContemporaryGroupId'] = this.carcassWeightContemporaryGroupData.traitGroupCode
          data['yearlingWeightContemporaryGroupId'] = null
        } else {
          data['yearlingWeightContemporaryGroupId'] = this.carcassWeightContemporaryGroupData.traitGroupCode
          data['weaningWeightContemporaryGroupId'] = null
        }
      }
      data.groupName = this.contemporaryGroupName
      const { id, trait } = this.$route.params
      const res = await axios.put(`animalGenetics/updateContemporaryGroup/${trait}/${id}`, data)
      if(res.data.success) {
        this.$notify({
          title: this.$t('success'),
          text: this.$t('geneticsAndBreeding.updateSuccessMsg'),
          type: "success",
        });
        this.stopLoading();
        this.$router.push({ name: 'contemporaryGroups', query: { trait: this.trait } })
      } else {
        this.$notify({
          title: this.$t('error'),
          text: this.$t('geneticsAndBreeding.errorMsg'),
          type: "error",
        });
        this.stopLoading();
      }
    },
    async getGroupCodes() {
      let trait
      if (this.carcassTrait) {
        trait = this.carcassTrait
      } else if (this.trait === 'weaningWeight') {
        trait = 'birthWeight'
      } else if (this.trait === 'yearlingWeight') {
        trait = 'weaningWeight'
      }
      const res = await axios.get(`animalGenetics/getGroupCodes/${trait}`)
      if (res.data.success) {
        this.groupCodes = res.data.data
      }
      this.getRequiredFieldsSetting()
    },
    async deleteContemporaryGroup () {
      const res = await axios.delete(`animalGenetics/deleteContemporaryGroup/${this.trait}/${this.$route.params.id}`)
      if (res.data.success) {
        this.$router.push({ name: 'contemporaryGroups' })
        this.$notify({
          title: this.$t('geneticsAndBreeding.deleteSuccess'),
          text: this.$t('geneticsAndBreeding.deleteSuccessMsg'),
          type: "success",
        });
      } else {
        this.$notify({
          title: this.$t('geneticsAndBreeding.deleteFailed'),
          text: this.$t('geneticsAndBreeding.deleteFailedMsg'),
          type: "error",
        });
      }
    },
    formatDate(date, format = 'YYYY-MM-DD') {
      const d =  moment(date).format(format);
      if(d == 'Invalid date'){
        return ''
      }
      return d
    },
    async getRequiredFieldsSetting() {
      this.requiredFieldRules = {}
      this.requiredFieldsArray = {}
      const trait = this.traits.find(item => item.value === this.trait)
      const res = await axios.get(`animalGenetics/epdRequiredFieldsSetting/${trait.label}`)
      if (res.data.success) {
        this.requiredFields = res.data.data.requiredFields

        this.requiredFields.map(item => {
          const key = this.snakeCaseToCamelCase(item.id)
          this.requiredFieldsArray[key] = item.status
          if (item.status) {
            this.requiredFieldRules[key] = [ v => !!v || `${item.name} is required` ]
          }
        })
        this.$refs.form.resetValidation()
      } else {
        this.$notify({
          title: this.$t('error'),
          text: this.$t('geneticsAndBreeding.fetchErrorMsg'),
          type: "error",
        });
      }
    }
  },
  created() {
    this.trait = this.$route.params.trait
    this.getContemporaryGroupData()
    this.getGroupCodes()
    // this.getRequiredFieldsSetting()
  },
  mixins: [loadingMixin],
}
</script>
<style scoped>
.contemporary-group .v-card__body {
  min-height: 450px;
  background-color: #ffffff;
  margin: 16px;
}

.contemporary-group .grey-hr {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-bottom: none;
}

.contemporary-group .input-label {
  color: #1E1E1E;
  font-size: 12px;
  margin-bottom: 8px;
  display: block;
}

.contemporary-group .input-label i {
  font-size: 16px;
  margin-left: 3px;
}
::v-deep .required-field .v-input__slot {
  border: 1px solid #ffb443 !important;
  background-color: #fff7ec !important;
}
</style>